import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Button,
  MenuItem,
} from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { getCapitalActivityEventStateBadge } from "features/capital-activity/CapitalActivityUtils";
import { HypotheticalTabProps } from "features/financial-reporting/tabs/HypotheticalTabProps";
import { useCeleryWorkflowStatusRetrieve } from "gen/endpoints/celery-workflow-status/celery-workflow-status";
import {
  getEndOfPeriodReportRetrieveQueryKey,
  useEndOfPeriodReportRetrieveSuspense,
} from "gen/endpoints/end-of-period-report/end-of-period-report";
import {
  getHypotheticalWaterfallRetrieveQueryKey,
  useHypotheticalWaterfallDelete,
  useHypotheticalWaterfallDraftToComplete,
  useHypotheticalWaterfallRetrieveSuspense,
} from "gen/endpoints/financial-reporting/financial-reporting";
import { useFundListSuspense } from "gen/endpoints/fund-entities/fund-entities";
import {
  getFundFamilyConfigRetrieveQueryKey,
  useFundFamilyConfigRetrieveSuspense,
} from "gen/endpoints/fund-family-config/fund-family-config";
import {
  getFundFamilySubEventListQueryKey,
  investorPortalDataRetrieve,
  useFundFamilyEventRetrieve,
  useFundFamilySubEventListSuspense,
  useWaterfallAuditExcelWorkbookCreate,
} from "gen/endpoints/fund-family-event/fund-family-event";
import { getHypotheticalWaterfallOverviewRetrieveQueryKey } from "gen/endpoints/hypothetical-waterfall/hypothetical-waterfall";
import { CeleryWorkflowStateMachineFieldsEnum } from "gen/model/celeryWorkflowStateMachineFieldsEnum";
import { InvestorPortalTypeEnum } from "gen/model/investorPortalTypeEnum";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Restricted } from "shared/components/access/Restricted";
import { OutOfDateAlert } from "shared/components/alert/OutOfDateAlert";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { PageMenu } from "shared/components/layout/PageMenu";
import { PageTabs } from "shared/components/layout/PageTabs";
import { useConfirmationDialog } from "shared/components/modal/confirm/ConfirmationDialog";
import { DeleteConfirmationDialog } from "shared/components/modal/DeleteConfirmationDialog";
import { useToast } from "shared/components/toast/useToast";
import useDocumentPoll from "shared/hooks/useDocumentPoll";
import { useFeatureFlagActive } from "shared/hooks/useFeatureFlag";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { InfoCircleIcon } from "shared/icons/InfoCircleIcon";
import { rootRoute } from "shared/navigation/Routes";
import {
  getErrorMessageFromCeleryWorkflow,
  useShowInternalErrorDetails,
} from "shared/utils/ErrorUtils";
import { invalidateQueryKeys } from "shared/utils/QueryUtils";
import { downloadFileBlob, hyphensToUnderscores } from "shared/utils/utils";
import { StringParam, useQueryParam, withDefault } from "use-query-params";

export const HypotheticalWaterfallReviewPage: React.FC = () => {
  const { t } = useTranslation([
    "common",
    "financialReporting",
    "capitalActivity",
  ]);
  const { fundFamilyId, hypotheticalWaterfallId, endOfPeriodReportId } =
    useRequiredStringParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isInternalUser = useShowInternalErrorDetails();
  const { data: funds } = useFundListSuspense({
    fund_family_ids: [fundFamilyId],
    require_commitment: true,
  });
  const [fundId, setFundId] = useQueryParam(
    "fundId",
    withDefault(StringParam, funds[0].id),
  );
  const tabProps: HypotheticalTabProps = {
    fundFamilyId,
    hypotheticalWaterfallId,
    endOfPeriodReportId,
    fundId,
    setFundId,
  };

  const { data: endOfPeriodReport } = useEndOfPeriodReportRetrieveSuspense(
    fundFamilyId,
    endOfPeriodReportId,
  );

  const { mutateAsync: downloadAudit } = useWaterfallAuditExcelWorkbookCreate();

  const { data: fundFamilyEventData } = useFundFamilyEventRetrieve(
    fundFamilyId,
    endOfPeriodReport.fund_family_event_id,
    {
      query: {
        enabled: !!endOfPeriodReport,
      },
    },
  );

  const { data: subEvents } = useFundFamilySubEventListSuspense(
    fundFamilyId,
    endOfPeriodReport.fund_family_event_id,
    {},
  );

  const { data: fundFamilyConfig } =
    useFundFamilyConfigRetrieveSuspense(fundFamilyId);

  const { data: hypotheticalWaterfall } =
    useHypotheticalWaterfallRetrieveSuspense(
      fundFamilyId,
      endOfPeriodReportId,
      hypotheticalWaterfallId,
    );

  const { data: celeryWorkflow } = useCeleryWorkflowStatusRetrieve(
    hypotheticalWaterfall.celery_workflow_id,
    {
      query: {
        enabled: !!hypotheticalWaterfall,
      },
    },
  );

  const { mutateAsync: finalizeHypotheticalWaterfall } =
    useHypotheticalWaterfallDraftToComplete();

  const { mutateAsync: deleteHypotheticalWaterfall } =
    useHypotheticalWaterfallDelete();

  const financialReportingRoute = rootRoute({})
    .fundFamilies({})
    .fundFamily({ fundFamilyId })
    .financialReporting({});
  const navigateToProtectedCarryDashboard = () => {
    navigate(
      financialReportingRoute
        .hypotheticalWaterfall({
          hypotheticalWaterfallId,
          endOfPeriodReportId,
        })
        .designateProtectedCarry({}).$,
    );
  };

  const navigateToHypoEditPage = () => {
    navigate(
      financialReportingRoute.hypoWaterfallEdit({
        endOfPeriodReportId: hypotheticalWaterfall.end_of_period_report_id,
      }).$,
    );
  };

  const refetchAndNavigate = async () => {
    navigate(financialReportingRoute.$);
    await queryClient.invalidateQueries({
      queryKey: getEndOfPeriodReportRetrieveQueryKey(
        fundFamilyId,
        endOfPeriodReportId,
      ),
    });
    await queryClient.invalidateQueries({
      queryKey: getHypotheticalWaterfallOverviewRetrieveQueryKey(fundFamilyId),
    });
  };

  const onFinalize = async () => {
    await finalizeHypotheticalWaterfall({
      fundFamilyId,
      endOfPeriodReportId,
      hypotheticalWaterfallId,
    });

    await refetchAndNavigate();
  };

  const confirmDeleteEvent = useConfirmationDialog(
    DeleteConfirmationDialog as DeleteConfirmationDialog,
    {
      title: t("financialReporting:hypotheticalDeleteModal.title"),
      description: t("financialReporting:hypotheticalDeleteModal.description"),
      successMessage: t("financialReporting:hypotheticalDeleteModal.success"),
      performDelete: () =>
        deleteHypotheticalWaterfall({
          fundFamilyId,
          endOfPeriodReportId,
          hypotheticalWaterfallId,
        }),
    },
  );

  const { setDocumentId } = useDocumentPoll(
    t("waterfall:hypoWaterfallAudit.success"),
    t("waterfall:hypoWaterfallAudit.error"),
    t("waterfall:hypoWaterfallAudit.start"),
  );
  const refreshHypothetical = async () =>
    invalidateQueryKeys(
      queryClient,
      getEndOfPeriodReportRetrieveQueryKey(fundFamilyId, endOfPeriodReportId),
      getFundFamilySubEventListQueryKey(
        fundFamilyId,
        endOfPeriodReport.fund_family_event_id,
      ),
      getFundFamilyConfigRetrieveQueryKey(fundFamilyId),
      getHypotheticalWaterfallRetrieveQueryKey(
        fundFamilyId,
        endOfPeriodReportId,
        hypotheticalWaterfallId,
      ),
    );

  const discardHypothetical = async () => {
    const success = await confirmDeleteEvent();
    if (success) {
      await refetchAndNavigate();
    }
  };

  const { showErrorToast } = useToast();
  const isWaterfallSubevent = useFeatureFlagActive("waterfall_subevent");

  const subEvent = isWaterfallSubevent
    ? subEvents.find((event) => event.type === "waterfall")
    : subEvents.find(
        (event) => event.type === "distribution" && event.entity_id === fundId,
      );

  const downloadExport = async () => {
    const date = hyphensToUnderscores(fundFamilyEventData!.effective_date);
    const filename = `jsq_export_net_income_${date}.xlsx`;
    if (fundFamilyEventData?.id) {
      try {
        const excelExportBlob = await investorPortalDataRetrieve(
          fundFamilyId,
          fundFamilyEventData.id,
          subEvent!.id!,
        );
        downloadFileBlob(excelExportBlob, filename);
      } catch (err) {
        showErrorToast(err);
      }
    }
  };

  const downloadAuditExport = async () => {
    const workbookDocument = await downloadAudit({
      fundFamilyId,
      fundFamilyEventId: fundFamilyEventData!.id!,
      fundFamilySubEventId: subEvent!.id!,
      fundEntityId: fundId,
      params: {
        is_equalized_view: true,
      },
    });
    setDocumentId(workbookDocument.id);
  };

  const isDraftEvent = fundFamilyEventData?.state === "draft";

  const isHypoWaterfallv2 = useFeatureFlagActive(
    "hypo_waterfalls_with_custom_pnl_input",
  );

  const { data } = useFundFamilyConfigRetrieveSuspense(fundFamilyId);

  const hideEdit =
    isHypoWaterfallv2 &&
    data.config.hypothetical_pnl_input_level === "custom_entities";

  const isHypoInClawback = hypotheticalWaterfall.is_carry_clawback_available;
  const showJuniperExport =
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    fundFamilyConfig.config.investor_portal_type ==
    InvestorPortalTypeEnum.juniper_square;

  const stateBadge = getCapitalActivityEventStateBadge(
    fundFamilyEventData?.state,
    t,
  );

  return (
    <PageContent isFullHeight={true}>
      <PageHeader
        title={t("financialReporting:hypotheticalWaterfallEvent")}
        badge={
          <>
            {stateBadge && (
              <Badge colorScheme={stateBadge.color}>{stateBadge.text}</Badge>
            )}
            {endOfPeriodReport.date_range_rich_value && (
              <Badge colorScheme="default">
                {endOfPeriodReport.date_range_rich_value}
              </Badge>
            )}
          </>
        }
        backButton={{
          title: t("common:financialReporting"),
          path: rootRoute({})
            .fundFamilies({})
            .fundFamily({ fundFamilyId })
            .financialReporting({}).$,
        }}
        actions={
          <Restricted permission={PermissionTypeEnum.write}>
            <Button
              size="xs"
              colorScheme="secondary"
              onClick={downloadAuditExport}
            >
              {t("waterfall:waterfallAudit.download")}
            </Button>
            {isDraftEvent &&
              celeryWorkflow?.state ==
                CeleryWorkflowStateMachineFieldsEnum.complete && (
                <Button onClick={onFinalize} colorScheme="tertiary" size="sm">
                  {t("common:finalize")}
                </Button>
              )}
            {(isHypoInClawback || isDraftEvent || showJuniperExport) && (
              <PageMenu>
                {isDraftEvent && !hideEdit && (
                  <MenuItem onClick={navigateToHypoEditPage}>
                    {t("financialReporting:editHypotheticalWaterfall")}
                  </MenuItem>
                )}
                {isHypoInClawback && (
                  <MenuItem onClick={navigateToProtectedCarryDashboard}>
                    {t("financialReporting:viewProtectedCarryInfo")}
                  </MenuItem>
                )}
                {showJuniperExport && (
                  <MenuItem onClick={downloadExport}>
                    {t("capitalActivity:thirdPartyExports.juniperDownload")}
                  </MenuItem>
                )}
                {isDraftEvent && (
                  <MenuItem
                    className="destructive"
                    onClick={discardHypothetical}
                  >
                    {t("common:discardDraft")}
                  </MenuItem>
                )}
              </PageMenu>
            )}
          </Restricted>
        }
      />
      <PageBody>
        {!!fundFamilyEventData?.out_of_date_reason && (
          <OutOfDateAlert
            fundFamilyId={fundFamilyId}
            fundFamilyEventId={endOfPeriodReport.fund_family_event_id}
            message={fundFamilyEventData.out_of_date_reason}
            onDiscard={discardHypothetical}
            onRefresh={refreshHypothetical}
          />
        )}
        {celeryWorkflow?.state ==
          CeleryWorkflowStateMachineFieldsEnum.complete && (
          <>
            {hypotheticalWaterfall.show_discrepancy_banner && (
              <Alert status="info" mb="16px">
                <AlertIcon>
                  <InfoCircleIcon />
                </AlertIcon>
                <AlertDescription>
                  {t("financialReporting:showDiscrepancyBanner")}{" "}
                </AlertDescription>
              </Alert>
            )}
            <PageTabs context={tabProps} />
          </>
        )}
        {celeryWorkflow?.state ==
          CeleryWorkflowStateMachineFieldsEnum.failed && (
          <Alert status="error" mb="16px">
            <AlertIcon>
              <InfoCircleIcon />
            </AlertIcon>
            <AlertDescription>
              {isInternalUser
                ? getErrorMessageFromCeleryWorkflow(celeryWorkflow)
                : t("common:error.generic")}
            </AlertDescription>
          </Alert>
        )}
      </PageBody>
    </PageContent>
  );
};
